// SourceViewer — one source shown as it is (#89, the shared frame "Källvisare ·
// ram", E-06..E-16): a header (back to the Knowledge bank, the kind, the
// title, one meta line, Download original, "…"), the ORIGINAL in the centre
// with the referenced passages marked and numbered, and beside it the tabs
// "Used in · Facts · Details".
//
// The centre is the source's own viewer (`viewerOf`): PDF pages, the image,
// the player, the text of a text file. Anything the app can't show as it is
// gets the default (E-13): no preview, the text that was read, and the
// original to download.
//
// The marks follow the list being read: on "Used in" the statements that use
// the source, on "Facts" the passages the facts rest on. Each card and its
// marker carry the same number; selecting either selects the pair and brings
// its page into view.
//
// A projection + intents. Local state is view-ephemeral only: the tab, the
// selected number, the facts filter, whether the read text is unfolded.

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type { LoadedSource } from "../../app/ports/resource.js";
import type { SourceReadStatus } from "../../app/sourceReadStatus.js";
import type { Claim } from "../ClaimRow/ClaimRow.js";
import { Heading } from "../Heading/Heading.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import {
    fileKindIcon,
    useFileKindLabel,
} from "../KnowledgeBank/KnowledgeBank.js";
import {
    type FileKind,
    viewerOf,
} from "../KnowledgeBank/knowledgeBankModel.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";
import type { MaterialRef, PageSpan } from "../Navigation/types.js";
import type { SourceUse } from "../SourceView/types.js";
import { TextTabs } from "../TextTabs/TextTabs.js";
import { SourceOriginal } from "./SourceOriginal.js";
import { DetailsTab, FactsTab, UsedInTab } from "./SourceViewerTabs.js";
import {
    factsOf,
    formatSize,
    referencesOf,
    usedInCount,
    useGroupsOf,
    type ViewerTab,
} from "./sourceViewerModel.js";
import "./SourceViewer.css";

export type SourceViewerProps = {
    readonly id: string;
    // The title a reader knows it by (the list's); the stored name is in
    // `source.meta.filename`.
    readonly title: string;
    readonly fileKind: FileKind;
    readonly source: LoadedSource;
    readonly readStatus: SourceReadStatus | undefined;
    // The knowledge bank's claims resting on this source (its facts).
    readonly claims: readonly Claim[];
    readonly archived: boolean;
    // Where to open (a page a chat tool read, a cited page).
    readonly focusPages?: PageSpan;
    readonly onBack: () => void;
    // Opened on the Studio's canvas: the crumb leads back there first.
    readonly onBackToStudio?: () => void;
    // Open a use where it is (the material, at the statement), or a
    // material made from the source.
    readonly onOpenUse: (use: SourceUse) => void;
    readonly onOpenMaterial: (material: MaterialRef) => void;
    readonly onCreateMaterial: () => void;
    readonly onArchive: () => void;
    readonly onUnarchive: () => void;
    readonly onRemove: () => void;
    // Re-upload the original when its stored content is gone.
    readonly onReupload?: () => void;
};

export const SourceViewer = (props: SourceViewerProps): ReactNode => {
    const { t, i18n } = useLingui();
    const kindLabel = useFileKindLabel();
    const { id, title, source, claims, readStatus } = props;
    // View-ephemeral: the tab and the selected number.
    const [tab, setTab] = useState<ViewerTab>("used");
    const [selected, setSelected] = useState<number | undefined>(undefined);

    const viewer = viewerOf({
        fileKind: props.fileKind,
        content: source.content,
    });
    const groups = useGroupsOf(source.uses, source.derivedMaterials);
    const facts = factsOf(claims, id, source.uses);
    const references = referencesOf(tab, groups, facts);
    const selectedPage = references.find((r) => r.n === selected)?.page;
    const focusPages: PageSpan | undefined =
        selectedPage === undefined
            ? props.focusPages
            : { first: selectedPage, last: selectedPage };

    const chooseTab = (next: ViewerTab) => {
        setTab(next);
        setSelected(undefined);
    };

    // === The header ==========================================================
    const added = (() => {
        const iso = source.meta.addedOnIso;
        if (iso === undefined) return undefined;
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return undefined;
        const date = d.toLocaleDateString(i18n.locale, {
            day: "numeric",
            month: "short",
        });
        return t`added ${date}`;
    })();
    const readLine = (() => {
        switch (readStatus?.type) {
            case "source_reading_scanned_pages": {
                const pages = readStatus.pageCount;
                return t`${plural(pages, {
                    one: "reading # scanned page…",
                    other: "reading # scanned pages…",
                })}`;
            }
            case "source_read_failed":
                return t`couldn't be read`;
            case "source_read_ready":
            case undefined: {
                const n = claims.length;
                return n === 0
                    ? t`read`
                    : t`${plural(n, { one: "read, # fact", other: "read, # facts" })}`;
            }
            default:
                return exhaustSwitchCase(readStatus);
        }
    })();
    const meta = [
        kindLabel(props.fileKind),
        source.original === undefined
            ? undefined
            : formatSize(source.original.size, i18n.locale),
        added,
        readLine,
    ]
        .filter((part) => part !== undefined)
        .join(" · ");

    const menu: readonly MenuItem[] = [
        {
            _tag: "item",
            label: t`Create material from this source`,
            icon: <Icon name="sparkle" size="xs" />,
            onSelect: props.onCreateMaterial,
        },
        props.archived
            ? {
                  _tag: "item",
                  label: t`Unarchive`,
                  icon: <Icon name="archive" size="xs" />,
                  onSelect: props.onUnarchive,
              }
            : {
                  _tag: "item",
                  label: t`Archive`,
                  icon: <Icon name="archive" size="xs" />,
                  onSelect: props.onArchive,
              },
        {
            _tag: "item",
            label: t`Remove from the knowledge bank…`,
            icon: <Icon name="trash" size="xs" />,
            tone: "danger",
            onSelect: props.onRemove,
        },
    ];

    // === The centre ==========================================================
    const centre = (): ReactNode => (
        <SourceOriginal
            fileKind={props.fileKind}
            source={source}
            focusPages={focusPages}
            references={references}
            selected={selected}
            onMarker={setSelected}
            onReupload={props.onReupload}
        />
    );
    const note = (): string | undefined => {
        switch (viewer) {
            case "pdf":
            case "image":
            case "audio":
            case "video":
            case "text":
                return t`The original, unchanged`;
            case "word":
                return t`Original formatting`;
            case "default":
                return undefined;
            default:
                return exhaustSwitchCase(viewer);
        }
    };

    const used = usedInCount(groups);
    const tabBody = (): ReactNode => {
        switch (tab) {
            case "used":
                return (
                    <UsedInTab
                        groups={groups}
                        numbered={
                            viewer === "pdf" ||
                            viewer === "text" ||
                            viewer === "word" ||
                            viewer === "default"
                        }
                        selected={selected}
                        onSelect={setSelected}
                        onOpenUse={props.onOpenUse}
                        onOpenMaterial={props.onOpenMaterial}
                    />
                );
            case "facts":
                return (
                    <FactsTab
                        facts={facts}
                        selected={selected}
                        onSelect={setSelected}
                    />
                );
            case "details":
                return (
                    <DetailsTab
                        source={source}
                        kind={kindLabel(props.fileKind)}
                        size={
                            source.original === undefined
                                ? undefined
                                : formatSize(source.original.size, i18n.locale)
                        }
                        readLine={readLine}
                        onRemove={props.onRemove}
                    />
                );
            default:
                return exhaustSwitchCase(tab);
        }
    };

    return (
        <div className="sv">
            <header className="sv-head">
                <nav className="sv-crumb" aria-label={t`Breadcrumb`}>
                    {props.onBackToStudio === undefined ? null : (
                        <>
                            <button
                                type="button"
                                className="sv-crumb__back"
                                onClick={props.onBackToStudio}
                            >
                                <Icon name="arrow-left" size="sm" />
                                {t`Back to Studio`}
                            </button>
                            <span aria-hidden="true">·</span>
                        </>
                    )}
                    <button
                        type="button"
                        className="sv-crumb__back"
                        onClick={props.onBack}
                    >
                        <Icon name="arrow-left" size="sm" />
                        {t`Knowledge bank`}
                    </button>
                    <span aria-hidden="true">/</span>
                    <button
                        type="button"
                        className="sv-crumb__back"
                        onClick={props.onBack}
                    >
                        {t`Sources`}
                    </button>
                </nav>
                <div className="sv-head__row">
                    <span className="sv-head__icon" aria-hidden="true">
                        <Icon name={fileKindIcon(props.fileKind)} size="md" />
                    </span>
                    <div className="sv-head__text">
                        <Heading
                            level={1}
                            size="2xl"
                            className="sv-head__title"
                            title={title}
                        >
                            {title}
                        </Heading>
                        <p className="sv-head__meta">{meta}</p>
                    </div>
                    <div className="sv-head__actions">
                        {source.original === undefined ? null : (
                            <a
                                className="bilda-btn bilda-btn--secondary bilda-btn--md sv-download"
                                href={source.original.url}
                                download={source.meta.filename}
                            >
                                {t`Download original`}
                                <Icon name="download" size="sm" />
                            </a>
                        )}
                        <Menu
                            align="end"
                            trigger={
                                <IconButton
                                    label={t`More for ${title}`}
                                    variant="ghost"
                                >
                                    <Icon name="overflow" />
                                </IconButton>
                            }
                            items={menu}
                        />
                    </div>
                </div>
            </header>
            <div className="sv-body">
                <section
                    className="sv-centre"
                    data-viewer={viewer}
                    aria-label={t`The source`}
                >
                    {note() === undefined ? null : (
                        <p className="sv-centre__note">{note()}</p>
                    )}
                    {centre()}
                </section>
                <aside className="sv-side" aria-label={t`About the source`}>
                    <TextTabs
                        ariaLabel={t`About the source`}
                        value={tab}
                        onChange={chooseTab}
                        tabs={[
                            { value: "used", label: t`Used in  ${used}` },
                            {
                                value: "facts",
                                label: t`Facts  ${claims.length}`,
                            },
                            { value: "details", label: t`Details` },
                        ]}
                    />
                    <div className="sv-side__body">{tabBody()}</div>
                </aside>
            </div>
        </div>
    );
};
